import { OneEuroFilter, type OneEuroOptions } from './OneEuroFilter';
import type { BodyNodeLink, Node } from '../types';

interface NodeFilters {
  x: OneEuroFilter;
  y: OneEuroFilter;
  score: OneEuroFilter;
  wx: OneEuroFilter;
  wy: OneEuroFilter;
  wz: OneEuroFilter;
  wv: OneEuroFilter;
}

export interface PoseSmootherOptions extends OneEuroOptions {
  scoreMincutoff?: number;
  /** When true, the smoother lowers each node's `mincutoff` toward
   *  `lowConfidenceMincutoff` whenever the node's confidence is at or
   *  below `lowConfidenceScore`. Lower mincutoff = stronger smoothing,
   *  which suppresses jitter on occluded / partially visible joints
   *  without dulling the response when confidence is high. */
  confidenceAware?: boolean;
  lowConfidenceScore?: number;
  lowConfidenceMincutoff?: number;
}

export class PoseSmoother {
  private readonly filters = new Map<number, NodeFilters>();
  private opts: Required<PoseSmootherOptions>;

  constructor(opts: PoseSmootherOptions = {}) {
    this.opts = {
      mincutoff: opts.mincutoff ?? 1,
      beta: opts.beta ?? 0.08,
      dcutoff: opts.dcutoff ?? 1,
      scoreMincutoff: opts.scoreMincutoff ?? 2,
      confidenceAware: opts.confidenceAware ?? false,
      lowConfidenceScore: opts.lowConfidenceScore ?? 0.5,
      lowConfidenceMincutoff: opts.lowConfidenceMincutoff ?? 0.5,
    };
  }

  reset(): void {
    this.filters.clear();
  }

  setParams(opts: PoseSmootherOptions): void {
    this.opts = {
      ...this.opts,
      ...opts,
    };
    for (const f of this.filters.values()) {
      const p = {
        mincutoff: this.opts.mincutoff,
        beta: this.opts.beta,
        dcutoff: this.opts.dcutoff,
      };
      f.x.setParams(p);
      f.y.setParams(p);
      f.wx.setParams(p);
      f.wy.setParams(p);
      f.wz.setParams(p);
      f.score.setParams({ mincutoff: this.opts.scoreMincutoff, beta: 0 });
      f.wv.setParams({ mincutoff: this.opts.scoreMincutoff, beta: 0 });
    }
  }

  apply(bodies: readonly BodyNodeLink[], nowMs: number): BodyNodeLink[] {
    return bodies.map((body, personIdx) => ({
      nodes: body.nodes.map((n) => this.filterNode(personIdx, n, nowMs)),
      links: body.links,
      fps: body.fps,
      inferenceTimeMs: body.inferenceTimeMs,
    }));
  }

  private filterNode(personIdx: number, node: Node, nowMs: number): Node {
    const key = personIdx * 1000 + node.id;
    let f = this.filters.get(key);
    if (!f) {
      const p = {
        mincutoff: this.opts.mincutoff,
        beta: this.opts.beta,
        dcutoff: this.opts.dcutoff,
      };
      f = {
        x: new OneEuroFilter(p),
        y: new OneEuroFilter(p),
        score: new OneEuroFilter({ mincutoff: this.opts.scoreMincutoff, beta: 0 }),
        wx: new OneEuroFilter(p),
        wy: new OneEuroFilter(p),
        wz: new OneEuroFilter(p),
        wv: new OneEuroFilter({ mincutoff: this.opts.scoreMincutoff, beta: 0 }),
      };
      this.filters.set(key, f);
    }

    // Confidence-aware smoothing: occluded / partially visible joints
    // are jitter-prone, so we crank the mincutoff *down* (= stronger
    // smoothing) whenever the score dips. Above the threshold we
    // restore the default mincutoff for crisp response on clean
    // detections. Re-applied per call so each frame can up- or
    // down-shift the cutoff individually.
    if (this.opts.confidenceAware) {
      const target =
        node.score <= this.opts.lowConfidenceScore
          ? this.opts.lowConfidenceMincutoff
          : this.opts.mincutoff;
      const p = { mincutoff: target, beta: this.opts.beta, dcutoff: this.opts.dcutoff };
      f.x.setParams(p);
      f.y.setParams(p);
      f.wx.setParams(p);
      f.wy.setParams(p);
      f.wz.setParams(p);
    }

    const out: Node = {
      id: node.id,
      name: node.name,
      x: f.x.filter(node.x, nowMs),
      y: f.y.filter(node.y, nowMs),
      score: f.score.filter(node.score, nowMs),
    };
    if (typeof node.worldX === 'number') {
      out.worldX = f.wx.filter(node.worldX, nowMs);
      out.worldY = f.wy.filter(node.worldY ?? 0, nowMs);
      out.worldZ = f.wz.filter(node.worldZ ?? 0, nowMs);
      if (typeof node.worldVisibility === 'number') {
        out.worldVisibility = f.wv.filter(node.worldVisibility, nowMs);
      }
    }
    return out;
  }
}
